import { useCallback } from 'react';

import TagOptionNode from '@/features/Feedback/field/Tag/TagOptionNode';
import { ROOT_TAG_ALIAS } from '@/services/tag/converters';
import useTagStore from '@/stores/TagStore';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';

const getLv1TagNodes = (tagMap: TagMapT) =>
  Object.values(tagMap).filter((tag) => tag.level === 1);

const getTagFilterList = (tagMap: TagMapT) => {
  const res = getLv1TagNodes(tagMap);
  return res.reduce((pv, cv) => {
    const arr = [...pv];
    arr.push(cv.alias);
    cv.children.forEach((val) => {
      const tag = tagMap[val];
      if (tag) {
        arr.push(val);
        tag.children.forEach((val) => {
          const tag = tagMap[val];
          if (tag) {
            arr.push(val);
          }
        });
      }
    });
    return arr;
  }, [] as string[]);
};

const getTagPath = (tagMap: TagMapT, alias: string) => {
  const target = tagMap[alias];
  if (!target) return [];
  const parentArr = getTagParentsArr(tagMap, alias);
  parentArr.push(target);
  return parentArr;
};

const getTagParentsArr = (tagMap: TagMapT, alias: string) => {
  const findParentTagRecursive = (tagMap: TagMapT, target: TagT): TagT[] => {
    const findParents = (
      current: TagT | undefined,
      parents: TagT[] = [],
    ): TagT[] => {
      if (!current || !current.parent) {
        return parents;
      }

      const parent = tagMap[current.parent];
      if (!parent || parent.alias === ROOT_TAG_ALIAS) {
        return parents;
      }

      return findParents(parent, [parent, ...parents]);
    };

    return findParents(target);
  };
  const target = tagMap[alias];
  if (!target) return [];
  return findParentTagRecursive(tagMap, target);
};

const getTagFilterTree = (tagMap: TagMapT) => {
  const lv1 = getLv1TagNodes(tagMap).map((t) => t.alias);
  const mapper = (alias: string): FilterOptionItemT => {
    const node = tagMap[alias];
    const paths = getTagPath(tagMap, alias).map((t) => t.name);
    const parents = paths.slice(0, -1);
    return {
      columnKey: FEEDBACK_COLUMNS.TAG,
      filterKey: node.alias,
      filterName: paths.join(' / '),
      filterList: node.children.map(mapper),
      filterOptionNode: <TagOptionNode tag={node} parentNames={parents} />,
    };
  };
  return lv1.map(mapper);
};

const useTagPath = () => {
  const tagMap = useTagStore((state) => state.tagMap);
  const getPath = useCallback(
    (alias: string) => getTagPath(tagMap, alias).map((t) => t.name),
    [tagMap],
  );

  return getPath;
};

export {
  getLv1TagNodes,
  getTagFilterList,
  getTagFilterTree,
  getTagParentsArr,
  getTagPath,
  useTagPath,
};
